import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FOCUS_RING, GROUP_EDGE, META_LABEL } from "./coverageStyles";

const TH = "px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500";
const NUM = "px-4 py-4 text-right text-base font-black tabular-nums";

// Above one school there are no person rows to show — the roster is the whole
// network's staff, and a page of it answers nothing. One row per school does.
//
// The payload iterates the same org list the school matrix does, so the head
// office itself comes back as a row with nothing in it. Dropping the rows that
// are empty in every column keeps the table to the places visits actually
// happen, without inventing a "is this a school" rule the backend does not have.
const hasAnything = (school) =>
  (school.people || 0) > 0 ||
  (school.official || 0) > 0 ||
  (school.peer || 0) > 0 ||
  (school.overdue || 0) > 0 ||
  (school.declined || 0) > 0 ||
  (school.withdrawn || 0) > 0;

// A gap column: rose only when there is a gap, and it carries the school's name
// on the same row, so the colour is never the only way to find it.
const gapTone = (value) => (value > 0 ? "text-rose-600" : "text-slate-400");

export default function SchoolRollup({ schools = [], onPick }) {
  const { t } = useTranslation();
  const rows = useMemo(() => schools.filter(hasAnything), [schools]);

  if (rows.length === 0) {
    return (
      <div className="px-6 py-14 text-center">
        <p className="text-sm font-black text-slate-700">{t("coverage.emptyNetworkTitle")}</p>
        <p className="mx-auto mt-1 max-w-sm text-xs font-medium text-slate-500">
          {t("coverage.emptyNetworkBody")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="border-b border-slate-100 px-5 py-4">
        <h3 className="text-lg font-bold text-slate-900">{t("coverage.rollupTitle")}</h3>
        <p className="mt-0.5 text-xs font-medium text-slate-500">{t("coverage.networkPrompt")}</p>
      </div>

      {/* The rollup lost its tabs along with the table below it: these counts
          were side-relative too, so a network could previously answer only one
          half of its own question. Both gaps are columns now, named in the same
          words as the filter options a reader will use after drilling in. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <th scope="col" className={TH}>
                {t("coverage.rollupSchool")}
              </th>
              <th scope="col" className={`${TH} text-right`}>
                {t("coverage.totals.people")}
              </th>
              <th scope="col" className={`${TH} ${GROUP_EDGE} text-right`}>
                {t("coverage.filter.noneConducted")}
              </th>
              <th scope="col" className={`${TH} ${GROUP_EDGE} text-right`}>
                {t("coverage.filter.uncovered")}
              </th>
              <th scope="col" className={`${TH} ${GROUP_EDGE} text-right`}>
                {t("coverage.totals.official")}
              </th>
              <th scope="col" className={`${TH} text-right`}>
                {t("coverage.totals.peer")}
              </th>
              <th scope="col" className={`${TH} text-right`}>
                {t("coverage.totals.overdue")}
              </th>
              <th scope="col" className={`${TH} text-right`}>
                {t("coverage.col.closed")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((school) => (
              <tr key={school.organization?.uuid} className="hover:bg-slate-50/60">
                <th scope="row" className="px-4 py-4 text-left font-normal">
                  <button
                    type="button"
                    onClick={() => onPick(school.organization?.uuid)}
                    className={`-m-1 rounded-md p-1 text-left text-sm font-bold text-slate-900 transition hover:text-indigo-700 hover:underline ${FOCUS_RING}`}
                  >
                    {school.organization?.name}
                  </button>
                </th>
                <td className={`${NUM} text-slate-900`}>{school.people ?? 0}</td>
                {/* Each gap prints against the population it is drawn from —
                    the people who may conduct, and the people who may be
                    observed. "8" alone cannot say whether that is eight of
                    eight or eight of eighty. */}
                <td className={`${NUM} ${GROUP_EDGE} ${gapTone(school.none_conducted)}`}>
                  {school.none_conducted ?? 0}
                  <span className="ml-1 text-xs font-bold text-slate-400">
                    {t("coverage.tiles.ofTotal", { total: school.can_conduct ?? 0 })}
                  </span>
                </td>
                <td className={`${NUM} ${GROUP_EDGE} ${gapTone(school.uncovered)}`}>
                  {school.uncovered ?? 0}
                  <span className="ml-1 text-xs font-bold text-slate-400">
                    {t("coverage.tiles.ofTotal", { total: school.can_be_observed ?? 0 })}
                  </span>
                </td>
                <td className={`${NUM} ${GROUP_EDGE} text-slate-900`}>{school.official ?? 0}</td>
                <td className={`${NUM} text-slate-400`}>{school.peer ?? 0}</td>
                <td className={`${NUM} ${school.overdue > 0 ? "text-amber-700" : "text-slate-400"}`}>
                  {school.overdue ?? 0}
                </td>
                <td className="px-4 py-4 text-right text-[11px] font-semibold tabular-nums text-slate-400">
                  {t("coverage.col.closedShort", {
                    declined: school.declined ?? 0,
                    withdrawn: school.withdrawn ?? 0,
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="divide-y divide-slate-100 md:hidden">
        {rows.map((school) => (
          <li key={school.organization?.uuid} className="px-5 py-4">
            <button
              type="button"
              onClick={() => onPick(school.organization?.uuid)}
              className={`-m-1 rounded-md p-1 text-left text-sm font-bold text-slate-900 ${FOCUS_RING}`}
            >
              {school.organization?.name}
            </button>
            {/* Two gaps, then what actually happened. Four short figures in two
                rows read at 390px where six in three columns do not. */}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <p className={META_LABEL}>{t("coverage.filter.noneConducted")}</p>
                <p className={`mt-0.5 text-lg font-black tabular-nums ${gapTone(school.none_conducted)}`}>
                  {school.none_conducted ?? 0}
                  <span className="ml-1 text-xs font-bold text-slate-400">
                    {t("coverage.tiles.ofTotal", { total: school.can_conduct ?? 0 })}
                  </span>
                </p>
              </div>
              <div>
                <p className={META_LABEL}>{t("coverage.filter.uncovered")}</p>
                <p className={`mt-0.5 text-lg font-black tabular-nums ${gapTone(school.uncovered)}`}>
                  {school.uncovered ?? 0}
                  <span className="ml-1 text-xs font-bold text-slate-400">
                    {t("coverage.tiles.ofTotal", { total: school.can_be_observed ?? 0 })}
                  </span>
                </p>
              </div>
              <div>
                <p className={META_LABEL}>{t("coverage.totals.official")}</p>
                <p className="mt-0.5 text-lg font-black tabular-nums text-slate-900">
                  {school.official ?? 0}
                </p>
              </div>
              <div>
                <p className={META_LABEL}>{t("coverage.totals.overdue")}</p>
                <p
                  className={`mt-0.5 text-lg font-black tabular-nums ${
                    school.overdue > 0 ? "text-amber-700" : "text-slate-400"
                  }`}
                >
                  {school.overdue ?? 0}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
